import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Dialog } from "@headlessui/react";
import { XMarkIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { SelectorListSkeleton } from "../ResourceLoading";
import { ResourceError } from "../ResourceError";
import { ResourceEmpty } from "../ResourceEmpty";
import { useSelector, useDispatch } from "react-redux";
import { debounce } from "lodash";
import { getContactsAsync } from "../../../slices/customer/contact";
import { useAutofocusOnOpen } from "../useAutofocusOnOpen";

export const MultiContactSelector = ({
  open,
  onClose,
  selectedContacts = [],
  onContactsSelected,
  label = "Velg kontakter",
  placeholder = "Søk i kontakter...",
  customerId = null,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [contacts, setContacts] = useState([]);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedContactIds, setSelectedContactIds] = useState(
    selectedContacts.map((contact) => contact.id)
  );
  const searchInputRef = useAutofocusOnOpen(open);

  const itemState = useSelector((store) => store.contacts);

  useEffect(() => {
    if (open) {
      loadContacts();
    }
  }, [open, itemState.data]);

  useEffect(() => {
    if (itemState.data && itemState.data.length > 0) {
      setContacts(itemState.data);
    }
  }, [itemState.data]);

  useEffect(() => {
    filterContacts();
  }, [contacts, searchQuery]);

  const loadContacts = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const controller = {
        query: searchQuery || "",
        page: 1,
        per_page: 50,
        sortBy: "created_at",
        sort: "desc",
        filters: customerId ? [{ name: "CUSTOMER", value: customerId }] : [],
        view: null,
      };

      await dispatch(getContactsAsync(controller));
      setIsLoading(false);
    } catch (err) {
      console.error("Error loading contacts:", err);
      setError("Kunne ikke laste kontakter");
      setIsLoading(false);
    }
  };

  const filterContacts = () => {
    let filtered = contacts;

    if (searchQuery.trim()) {
      filtered = contacts.filter(
        (contact) =>
          contact.displayTitle
            ?.toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          contact.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          contact.mobile?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredContacts(filtered);
  };

  const handleSearchChange = debounce((query) => {
    setSearchQuery(query);
    // Reload contacts with new search query
    if (open) {
      loadContacts();
    }
  }, 300);

  const handleContactToggle = (contact) => {
    const newSelectedIds = [...selectedContactIds];
    const index = newSelectedIds.indexOf(contact.id);

    if (index > -1) {
      newSelectedIds.splice(index, 1);
    } else {
      newSelectedIds.push(contact.id);
    }

    setSelectedContactIds(newSelectedIds);
  };

  const handleRemoveAll = () => {
    setSelectedContactIds([]);
  };

  const handleConfirm = () => {
    const selectedContactsList = contacts.filter((contact) =>
      selectedContactIds.includes(contact.id)
    );
    onContactsSelected(selectedContactsList);
    onClose();
  };

  const isContactSelected = (contact) => {
    return selectedContactIds.includes(contact.id);
  };

  const renderContent = () => {
    if (isLoading) {
      return <SelectorListSkeleton />;
    }

    if (error) {
      return <ResourceError message={error} onRetry={loadContacts} />;
    }

    if (!filteredContacts || filteredContacts.length === 0) {
      return (
        <ResourceEmpty
          message={
            searchQuery
              ? t("Ingen kontakter funnet")
              : t("Ingen kontakter tilgjengelig")
          }
        />
      );
    }

    return (
      <div className="space-y-4 flex h-full min-h-0 flex-col">
        {/* Search Input */}
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            ref={searchInputRef}
            type="text"
            placeholder={placeholder}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            onChange={(e) => handleSearchChange(e.target.value)}
          />
        </div>

        {/* Selected Contacts Summary */}
        {selectedContactIds.length > 0 && (
          <div className="bg-blue-50 p-3 rounded-md">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-blue-900">
                {t("Valgte kontakter")}: {selectedContactIds.length}
              </span>
              <button
                onClick={handleRemoveAll}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                {t("Fjern alle")}
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {contacts
                .filter((contact) => selectedContactIds.includes(contact.id))
                .map((contact) => (
                  <span
                    key={contact.id}
                    className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                  >
                    {contact.displayTitle}
                    <button
                      onClick={() => handleContactToggle(contact)}
                      className="ml-1 text-blue-600 hover:text-blue-800"
                    >
                      ×
                    </button>
                  </span>
                ))}
            </div>
          </div>
        )}

        {/* Contacts List */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          <ul className="divide-y divide-gray-200">
            {filteredContacts.map((contact) => (
              <li key={contact.id}>
                <button
                  onClick={() => handleContactToggle(contact)}
                  className={`w-full text-left px-4 py-3 hover:bg-gray-50 focus:bg-gray-50 focus:outline-none ${
                    isContactSelected(contact)
                      ? "bg-blue-50 border-l-4 border-blue-500"
                      : ""
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-gray-900">
                        {contact.displayTitle}
                      </div>
                      <div className="text-sm text-gray-500">
                        {contact.email && <div>{contact.email}</div>}
                        {contact.mobile && <div>{contact.mobile}</div>}
                        {contact.customer && (
                          <div className="text-xs text-gray-400">
                            {contact.customer.displayTitle}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center">
                      {isContactSelected(contact) && (
                        <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                          <svg
                            className="w-3 h-3 text-white"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                      )}
                    </div>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      className="fixed inset-0 z-50 overflow-y-auto"
    >
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />

        <span
          className="hidden sm:inline-block sm:align-middle sm:h-screen"
          aria-hidden="true"
        >
          &#8203;
        </span>

        <div className="inline-flex w-full flex-col align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all max-h-[calc(100dvh-1rem)] sm:my-8 sm:align-middle sm:max-w-lg sm:h-[720px] sm:max-h-[80vh]">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4 flex-1 min-h-0 overflow-hidden">
            <div className="flex items-center justify-between mb-4">
              <Dialog.Title
                as="h3"
                className="text-lg leading-6 font-medium text-gray-900"
              >
                {label}
              </Dialog.Title>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {renderContent()}
          </div>

          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              onClick={handleConfirm}
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
            >
              {t("Bekreft")}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
            >
              {t("Avbryt")}
            </button>
          </div>
        </div>
      </div>
    </Dialog>
  );
};
